import React from "react";
import {
  usePutActivityFindFilterQuery,
  usePutActivityTemplateEmailFindFilterQuery,
} from "../../../app/useGeneratedApi";
import useBreakpoints from "../../../hooks/useBreakpoints";

interface UseTemplateSectionQueryOptions {
  searchKeyword?: string;
  favorited?: boolean;
  enabled?: boolean;
  tagIds?: string[];
  refetchSections?: string[];
}

export function useTemplateSectionQuery(
  options: UseTemplateSectionQueryOptions
) {
  const { searchKeyword, favorited, tagIds, enabled, refetchSections } =
    options;

  const { mdUp } = useBreakpoints();

  const {
    data: {
      content: savedTemplates = [],
      totalElements: totalSaved = 0,
      last: isLastSaved,
    } = {},
    isSuccess: isSavedSuccess,
    refetch: refetchSaved,
    remove: removeSaved,
    isLoading: isLoadingSaved,
  } = usePutActivityFindFilterQuery(
    {
      editorType: "DRAG_AND_DROP",
      isFavorite: favorited,
      searchNameText: searchKeyword,
      tagIds,
    },
    {
      page: 0,
      size: 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled,
    }
  );

  const {
    data: {
      content: staffPicksTemplates = [],
      totalElements: totalStaffPicks = 0,
      last: isLastStaffPicks,
    } = {},
    isSuccess: isStaffPicksSuccess,
    refetch: refetchStaffPicks,
    remove: removeStaffPicks,
    isLoading: isLoadingStaffPicks,
  } = usePutActivityTemplateEmailFindFilterQuery(
    {
      isFavorite: favorited,
      searchNameText: searchKeyword,
      types: ["STAFF_PICKED"],
      tagIds,
    },
    {
      page: 0,
      size: mdUp ? (savedTemplates.length > 0 ? 3 : 6) : 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled: enabled && (!mdUp || isSavedSuccess),
    }
  );

  const {
    data: {
      content: automationTemplates = [],
      totalElements: totalAutomation = 0,
      last: isLastAutomation,
    } = {},
    isSuccess: isAutomationSuccess,
    refetch: refetchAutomation,
    remove: removeAutomation,
    isLoading: isLoadingAutomation,
  } = usePutActivityTemplateEmailFindFilterQuery(
    {
      isFavorite: favorited,
      searchNameText: searchKeyword,
      types: ["AUTOMATION"],
      tagIds,
    },
    {
      page: 0,
      size: mdUp ? 6 : 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled,
    }
  );

  const {
    data: {
      content: colorfulTemplates = [],
      totalElements: totalColorful = 0,
      last: isLastColorful,
    } = {},
    isSuccess: isColorfulSuccess,
    refetch: refetchColorful,
    remove: removeColorful,
    isLoading: isLoadingColorful,
  } = usePutActivityTemplateEmailFindFilterQuery(
    {
      isFavorite: favorited,
      searchNameText: searchKeyword,
      types: ["COLORFUL"],
      tagIds,
    },
    {
      page: 0,
      size: mdUp ? 6 : 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled,
    }
  );

  const {
    data: {
      content: professionalTemplates = [],
      totalElements: totalProfessional = 0,
      last: isLastProfessional,
    } = {},
    isSuccess: isProfessionalSuccess,
    refetch: refetchProfessional,
    remove: removeProfessional,
    isLoading: isLoadingProfessional,
  } = usePutActivityTemplateEmailFindFilterQuery(
    {
      isFavorite: favorited,
      searchNameText: searchKeyword,
      types: ["PROFESSIONAL"],
      tagIds,
    },
    {
      page: 0,
      size: mdUp ? 6 : 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled,
    }
  );

  const {
    data: {
      content: minimalistTemplates = [],
      totalElements: totalMinimalist = 0,
      last: isLastMinimalist,
    } = {},
    isSuccess: isMinimalistSuccess,
    refetch: refetchMinimalist,
    remove: removeMinimalist,
    isLoading: isLoadingMinimalist,
  } = usePutActivityTemplateEmailFindFilterQuery(
    {
      isFavorite: favorited,
      searchNameText: searchKeyword,
      types: ["MINIMALIST"],
      tagIds,
    },
    {
      page: 0,
      size: mdUp ? 6 : 3,
      sort: ["updatedAt,DESC"],
    },
    {
      enabled,
    }
  );

  const refetchTemplates = React.useCallback(async () => {
    return Promise.all([
      refetchSaved(),
      refetchStaffPicks(),
      refetchAutomation(),
      refetchColorful(),
      refetchProfessional(),
      refetchMinimalist(),
    ]);
  }, [
    refetchSaved,
    refetchStaffPicks,
    refetchAutomation,
    refetchColorful,
    refetchProfessional,
    refetchMinimalist,
  ]);

  const removeTemplates = React.useCallback(async () => {
    removeSaved();
    removeStaffPicks();
    removeAutomation();
    removeColorful();
    removeProfessional();
    removeMinimalist();
  }, [
    removeSaved,
    removeStaffPicks,
    removeAutomation,
    removeColorful,
    removeProfessional,
    removeMinimalist,
  ]);

  React.useEffect(() => {
    refetchSections?.forEach((section) => {
      if (section === "Saved") {
        refetchSaved();
      } else if (section === "Staff picks") {
        refetchStaffPicks();
      } else if (section === "Automation") {
        refetchAutomation();
      } else if (section === "Colorful") {
        refetchColorful();
      } else if (section === "Professional") {
        refetchProfessional();
      } else if (section === "Minimalist") {
        refetchMinimalist();
      }
    });
  }, [
    refetchSaved,
    refetchSections,
    refetchStaffPicks,
    refetchAutomation,
    refetchColorful,
    refetchProfessional,
    refetchMinimalist,
  ]);

  const isEmpty =
    isSavedSuccess &&
    isStaffPicksSuccess &&
    isAutomationSuccess &&
    isColorfulSuccess &&
    isProfessionalSuccess &&
    isMinimalistSuccess &&
    savedTemplates.length === 0 &&
    staffPicksTemplates.length === 0 &&
    automationTemplates.length === 0 &&
    colorfulTemplates.length === 0 &&
    professionalTemplates.length === 0 &&
    minimalistTemplates.length === 0;

  const shouldShowSavedAndStaffPicks =
    isSavedSuccess &&
    isStaffPicksSuccess &&
    (savedTemplates.length > 0 || staffPicksTemplates.length > 0);
  const shouldShowSaved = isSavedSuccess && savedTemplates.length > 0;
  const shouldShowAutomation =
    isAutomationSuccess && automationTemplates.length > 0;
  const shouldShowColorful = isColorfulSuccess && colorfulTemplates.length > 0;
  const shouldShowProfessional =
    isProfessionalSuccess && professionalTemplates.length > 0;
  const shouldShowMinimalist =
    isMinimalistSuccess && minimalistTemplates.length > 0;

  return {
    savedTemplates,
    staffPicksTemplates,
    automationTemplates,
    colorfulTemplates,
    professionalTemplates,
    minimalistTemplates,
    shouldShowSavedAndStaffPicks,
    shouldShowSaved,
    shouldShowAutomation,
    shouldShowColorful,
    shouldShowProfessional,
    shouldShowMinimalist,
    totalSaved,
    totalStaffPicks,
    totalAutomation,
    totalColorful,
    totalProfessional,
    totalMinimalist,
    isEmpty,
    isLastSaved,
    isLastStaffPicks,
    isLastAutomation,
    isLastColorful,
    isLastProfessional,
    isLastMinimalist,
    isLoadingSaved,
    isLoadingStaffPicks,
    isLoadingAutomation,
    isLoadingColorful,
    isLoadingProfessional,
    isLoadingMinimalist,
    refetchTemplates,
    removeTemplates,
  };
}
